import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import {
  GraduationCap,
  CalendarDays,
  TrendingUp,
  AlertTriangle,
  AlertCircle,
  Loader2,
  Printer
} from 'lucide-react';
import { toast } from 'react-toastify';
import { LineChart, Line, XAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { getConsultantVulnerabilities, getAuthToken } from '../logic/consultantVulnerabilities';
import { API_CONFIG } from '../config/api';
import { getVulnerabilityAssessments, getDefaultAssessments, generateVulnerabilityAssessments } from '../logic/vulnerabilityAssessment';
import type { GroupVulnerabilities } from '../logic/consultantVulnerabilities';
import type { VulnerabilityAssessment } from '../logic/vulnerabilityAssessment';

type DataScope = 'consultant' | 'enterprise';

interface VulnerabilitiesPageProps {
  scope?: DataScope;
}

const ConsultantVulnerabilitiesPage: React.FC<VulnerabilitiesPageProps> = ({ scope = 'consultant' }) => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const [vulnerabilitiesData, setVulnerabilitiesData] = useState<GroupVulnerabilities[]>([]);
  const [assessments, setAssessments] = useState<VulnerabilityAssessment>(getDefaultAssessments());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [daysFilter, setDaysFilter] = useState(30);
  type OwnershipFilter = 'enterprise_owned' | 'locally_owned';
  const [ownershipFilter, setOwnershipFilter] = useState<OwnershipFilter>('locally_owned');
  type DataSource = 'sowb' | 'assessments';
  const [dataSource, setDataSource] = useState<DataSource>('sowb');
  const [assessmentsRefreshing, setAssessmentsRefreshing] = useState(false);
  const [trendData, setTrendData] = useState<any[]>([]);

  // Fetch vulnerabilities data and AI assessments
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = getAuthToken();
        if (!token) {
          setError('Authentication required');
          return;
        }

        let ownershipPayload = ownershipFilter === 'enterprise_owned' ? 'enterpriseOwned' : 'locallyOwned';
        if (user?.role !== 'Admin') {
          // If the user isn't an admin, lock the ownership query to their specific enterprise status
          ownershipPayload = (user?.role === 'Enterprise' || user?.createdBy) ? 'enterpriseOwned' : 'locallyOwned';
        }

        const response = await getConsultantVulnerabilities({ token, days: daysFilter, ownership: ownershipPayload as any, source: dataSource });
        setVulnerabilitiesData(response.data);

        // Fetch score trends for the line chart
        const trendParams = new URLSearchParams({
          timeframe: daysFilter === 30 ? '30d' : daysFilter === 90 ? '90d' : '180d',
          scope: 'cohort',
          source: dataSource
        });

        try {
          const trendRes = await fetch(`${API_CONFIG.ENDPOINTS.ASSESSMENTS_ADMIN_SCORE_TRENDS}?${trendParams}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          if (trendRes.ok) {
            const trendJson = await trendRes.json();
            if (trendJson.success && trendJson.data?.trends) {
              setTrendData(trendJson.data.trends);
            }
          }
        } catch (e) {
          console.error("Failed to fetch score trends", e);
        }

        // Build summary of top lowest groups (by well-being) for AI-guided comments
        const sortedByWellbeing = [...response.data].sort(
          (a, b) => a.overallPercent0to100 - b.overallPercent0to100
        );
        const topLowest = sortedByWellbeing.slice(0, 10);
        const groupsSummary = topLowest.map((g) => ({
          groupName: g.groupName,
          overallPercent0to100: g.overallPercent0to100,
          primaryStressor: g.lowestAttributes[0]?.attribute || 'N/A'
        }));
        const stressorCounts: Record<string, number> = {};
        topLowest.forEach((g) => {
          const attr = g.lowestAttributes[0]?.attribute;
          if (attr) {
            stressorCounts[attr] = (stressorCounts[attr] || 0) + 1;
          }
        });

        // Fetch AI-generated assessments (or use defaults if AI unavailable)
        const generatedAssessments = await getVulnerabilityAssessments(
          token,
          `vulnerability_assessments_${daysFilter}_${ownershipFilter}_${dataSource}`,
          { groupCount: response.data.length, groupsSummary, stressorCounts }
        );
        setAssessments(generatedAssessments);
      } catch (err) {
        console.error('Failed to fetch vulnerabilities data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
        // Still show default assessments on error
        setAssessments(getDefaultAssessments());
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [daysFilter, ownershipFilter, dataSource]);

  // Handle date filter change
  const handleDateFilterChange = (newDays: number) => {
    setDaysFilter(newDays);
  };

  // Manually load AI suggestions for High Severity and Critical Alert (bypasses cache)
  const handleLoadAISuggestion = async () => {
    const token = getAuthToken();
    if (!token) return;
    setAssessmentsRefreshing(true);
    try {
      const sortedByWellbeing = [...vulnerabilitiesData].sort(
        (a, b) => a.overallPercent0to100 - b.overallPercent0to100
      );
      const topLowest = sortedByWellbeing.slice(0, 10);
      const groupsSummary = topLowest.map((g) => ({
        groupName: g.groupName,
        overallPercent0to100: g.overallPercent0to100,
        primaryStressor: g.lowestAttributes[0]?.attribute || 'N/A'
      }));
      const stressorCounts: Record<string, number> = {};
      topLowest.forEach((g) => {
        const attr = g.lowestAttributes[0]?.attribute;
        if (attr) {
          stressorCounts[attr] = (stressorCounts[attr] || 0) + 1;
        }
      });
      const groupData = {
        groupCount: vulnerabilitiesData.length,
        groupsSummary,
        stressorCounts
      };
      const { assessments: nextAssessments, source } = await generateVulnerabilityAssessments(token, groupData);
      setAssessments(nextAssessments);
      const cacheKey = `vulnerability_assessments_${daysFilter}_${ownershipFilter}_${dataSource}`;
      try {
        localStorage.setItem(cacheKey, JSON.stringify(nextAssessments));
        localStorage.setItem(`${cacheKey}_time`, Date.now().toString());
      } catch (e) {
        console.error('Error caching assessments:', e);
      }
      if (source === 'ai') {
        toast.success('AI suggestions updated');
      } else {
        toast.info('AI is not configured or unavailable; showing default suggestions.');
      }
    } catch (err) {
      toast.error('Failed to load AI suggestions');
    } finally {
      setAssessmentsRefreshing(false);
    }
  };

  // Helper function to format attribute names
  const formatAttributeName = (attribute: string): string => {
    const attributeMap: Record<string, string> = {
      spiritual: 'Spiritual',
      nutritional: 'Nutritional',
      physical: 'Physical',
      emotioPsychoRational: 'Emotional',
      legendarial: 'Legacy',
      religioTraditioCultural: 'Cultural',
      financial: 'Financial',
      environmental: 'Environmental',
      socioRelational: 'Social',
      occupational: 'Work'
    };
    return attributeMap[attribute] || attribute;
  };

  // Helper function to get risk score from overall average (0-100 scale)
  const getRiskScore = (overallPercent: number): number => {
    // Convert percentage to risk score where higher percentage = lower risk
    return Math.round(100 - overallPercent);
  };

  // Helper function to render assessment descriptions, parsing JSON if the AI returned an array of objects
  const renderAssessmentDescription = (desc: unknown, maxItems?: number) => {
    const strDesc = typeof desc === 'string' ? desc : String(desc);
    try {
      let parsed = JSON.parse(strDesc);

      // If AI returned a single object instead of an array, wrap it or extract its values
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        if (parsed.recommendations && Array.isArray(parsed.recommendations)) {
          parsed = parsed.recommendations;
        } else if (parsed.focus) {
          parsed = [{ description: parsed.focus }];
        } else if (parsed.message) {
          parsed = [{ description: parsed.message }];
        } else {
          // Convert object values to an array
          parsed = Object.values(parsed).map(v => ({ description: String(v) }));
        }
      }

      if (Array.isArray(parsed) && parsed.length > 0) {
        const itemsToRender = (maxItems && maxItems > 0 && parsed.length > maxItems)
          ? parsed.slice(0, maxItems)
          : parsed;

        return (
          <ul className="consultant-vulnerabilities__risk-list">
            {itemsToRender.map((item, idx) => {
              const text = item.description || item.reasoning || item.focus || item.message || JSON.stringify(item);
              return (
                <li key={idx} className="consultant-vulnerabilities__risk-item">
                  {item.group && <strong>{item.group}: </strong>}
                  {item.dimension && <strong>{item.dimension}: </strong>}
                  {typeof text === 'object' ? JSON.stringify(text) : String(text)}
                </li>
              );
            })}
          </ul>
        );
      }
    } catch (e) {
      // Fallback to rendering raw string
    }

    // Fallback: If it's a raw string containing newlines and maxItems is set, truncate by lines.
    if (maxItems && maxItems > 0 && strDesc.includes('\n')) {
      const lines = strDesc.split('\n').filter(line => line.trim().length > 0);
      if (lines.length > maxItems) {
        return (
          <ul className="consultant-vulnerabilities__risk-list">
            {lines.slice(0, maxItems).map((line, idx) => (
              <li key={idx} className="consultant-vulnerabilities__risk-item">{line.replace(/^[-*•]\s*/, '')}</li>
            ))}
          </ul>
        );
      }
    }

    return strDesc;
  };

  if (loading) {
    return (
      <div className={`consultant-vulnerabilities ${isDarkMode ? 'dark' : ''}`}>
        <div className="consultant-vulnerabilities__container">
          <div className="consultant-vulnerabilities__content">
            <div className="consultant-vulnerabilities__loading">
              <Loader2 className="consultant-vulnerabilities__loading-icon" />
              <p>Loading vulnerabilities data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`consultant-vulnerabilities ${isDarkMode ? 'dark' : ''}`}>
        <div className="consultant-vulnerabilities__container">
          <div className="consultant-vulnerabilities__content">
            <div className="consultant-vulnerabilities__error">
              <AlertTriangle className="consultant-vulnerabilities__error-icon" />
              <h3>Error Loading Data</h3>
              <p>{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`consultant-vulnerabilities ${isDarkMode ? 'dark' : ''}`}>
      <div className="consultant-vulnerabilities__container">
        <div className="consultant-vulnerabilities__content">
          <div className="consultant-vulnerabilities__header">
            <div className="consultant-vulnerabilities__header-info">
              <h1 className="consultant-vulnerabilities__title">
                SOWB Assessment - Vulnerabilities
              </h1>
              <p className="consultant-vulnerabilities__subtitle">
                <GraduationCap className="consultant-vulnerabilities__subtitle-icon" />
                Well-being score report
              </p>
            </div>
            <div className="consultant-vulnerabilities__header-controls">
              <div className="consultant-vulnerabilities__source-toggle-group">
                <button
                  className={`consultant-vulnerabilities__source-btn ${dataSource === 'sowb' ? 'consultant-vulnerabilities__source-btn--active' : ''}`}
                  onClick={() => setDataSource('sowb')}
                >
                  SOWB
                </button>
                <button
                  className={`consultant-vulnerabilities__source-btn ${dataSource === 'assessments' ? 'consultant-vulnerabilities__source-btn--active' : ''}`}
                  onClick={() => setDataSource('assessments')}
                >
                  Assessments
                </button>
              </div>
              <div className="consultant-vulnerabilities__date-filter-dropdown">
                <button className="consultant-vulnerabilities__date-filter">
                  <CalendarDays className="consultant-vulnerabilities__date-filter-icon" />
                  Last {daysFilter} Days
                </button>
                <div className="consultant-vulnerabilities__date-filter-options">
                  <button
                    className={`consultant-vulnerabilities__date-option ${daysFilter === 30 ? 'consultant-vulnerabilities__date-option--active' : ''}`}
                    onClick={() => handleDateFilterChange(30)}
                  >
                    Last 30 Days
                  </button>
                  <button
                    className={`consultant-vulnerabilities__date-option ${daysFilter === 90 ? 'consultant-vulnerabilities__date-option--active' : ''}`}
                    onClick={() => handleDateFilterChange(90)}
                  >
                    Last 90 Days
                  </button>
                  <button
                    className={`consultant-vulnerabilities__date-option ${daysFilter === 180 ? 'consultant-vulnerabilities__date-option--active' : ''}`}
                    onClick={() => handleDateFilterChange(180)}
                  >
                    Last 180 Days
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="consultant-vulnerabilities__grid">
            <div className="consultant-vulnerabilities__main-content">
              <div className="consultant-vulnerabilities__trend-card">
                <div className="consultant-vulnerabilities__trend-header">
                  <div className="consultant-vulnerabilities__trend-info">
                    <h3 className="consultant-vulnerabilities__trend-title">Risk Trend Indicators</h3>
                    <p className="consultant-vulnerabilities__trend-subtitle">Overall Assessment Summary</p>
                  </div>
                  <div className="consultant-vulnerabilities__trend-metric">
                    <p className="consultant-vulnerabilities__trend-value">
                      {vulnerabilitiesData.length > 0
                        ? `${Math.round(vulnerabilitiesData.reduce((sum, group) => sum + group.overallPercent0to100, 0) / vulnerabilitiesData.length)}%`
                        : '0%'
                      }
                    </p>
                    <p className="consultant-vulnerabilities__trend-status">
                      <TrendingUp className="consultant-vulnerabilities__trend-status-icon" />
                      Average Well-being Score
                    </p>
                  </div>
                </div>
                <div className="consultant-vulnerabilities__chart-container" style={{ height: '170px', marginTop: '1rem' }}>
                  {trendData && trendData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trendData} margin={{ top: 15, right: 15, left: 15, bottom: 5 }}>
                        <XAxis
                          dataKey="label"
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: '#64748b', fontSize: 13, fontWeight: 500 }}
                          dy={10}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#ffffff',
                            borderColor: '#e2e8f0',
                            borderRadius: '12px',
                            color: '#1e293b',
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                            padding: '12px 18px',
                            fontWeight: 600,
                            border: '1px solid #e2e8f0'
                          }}
                          itemStyle={{ color: '#0ea5e9', fontWeight: 700, fontSize: '15px' }}
                          formatter={(value: number | undefined) => value !== undefined ? [`${value}%`, 'Score'] : ['', 'Score']}
                          cursor={{ stroke: '#cbd5e1', strokeWidth: 1, strokeDasharray: '4 4' }}
                        />
                        <Line
                          type="monotone"
                          dataKey="score"
                          stroke="url(#colorLine)"
                          strokeWidth={4}
                          dot={{ r: 5, fill: '#ffffff', stroke: '#0ea5e9', strokeWidth: 3 }}
                          activeDot={{ r: 8, fill: '#0ea5e9', stroke: '#ffffff', strokeWidth: 3, style: { filter: 'drop-shadow(0px 3px 6px rgba(14,165,233,0.4))' } }}
                        />
                        <defs>
                          <linearGradient id="colorLine" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#38bdf8" />
                            <stop offset="100%" stopColor="#0284c7" />
                          </linearGradient>
                        </defs>
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="consultant-vulnerabilities__empty-chart">
                      <p>No trend data available for this period</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="consultant-vulnerabilities__sidebar">
              <div className="consultant-vulnerabilities__systemic-risks">
                <div className="consultant-vulnerabilities__systemic-risks-header">
                  <h2 className="consultant-vulnerabilities__section-title">Systemic Risks</h2>
                  <button
                    type="button"
                    className="consultant-vulnerabilities__systemic-risk-btn"
                    onClick={handleLoadAISuggestion}
                    disabled={assessmentsRefreshing}
                    aria-label="Load AI suggestion for systemic risk"
                  >
                    {assessmentsRefreshing ? (
                      <Loader2 className="consultant-vulnerabilities__systemic-risk-btn-icon consultant-vulnerabilities__systemic-risk-btn-icon--spin" size={16} />
                    ) : null}
                    <span>Systemic Risk</span>
                  </button>
                </div>
                <div className="consultant-vulnerabilities__risk-alert consultant-vulnerabilities__risk-alert--high">
                  <div className="consultant-vulnerabilities__risk-alert-header">
                    <AlertTriangle className="consultant-vulnerabilities__risk-alert-icon consultant-vulnerabilities__risk-alert-icon--high" />
                    <h4 className="consultant-vulnerabilities__risk-alert-title">{assessments.highSeverity.title}</h4>
                  </div>
                  <div className="consultant-vulnerabilities__risk-alert-text">
                    {renderAssessmentDescription(assessments.highSeverity.description, 1)}
                  </div>
                </div>
                <div className="consultant-vulnerabilities__risk-alert consultant-vulnerabilities__risk-alert--critical">
                  <div className="consultant-vulnerabilities__risk-alert-header">
                    <AlertCircle className="consultant-vulnerabilities__risk-alert-icon consultant-vulnerabilities__risk-alert-icon--critical" />
                    <h4 className="consultant-vulnerabilities__risk-alert-title">{assessments.criticalAlert.title}</h4>
                  </div>
                  <div className="consultant-vulnerabilities__risk-alert-text">
                    {renderAssessmentDescription(assessments.criticalAlert.description, 1)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="consultant-vulnerabilities__vulnerable-groups">
            <div className="consultant-vulnerabilities__vulnerable-groups-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 className="consultant-vulnerabilities__section-title" style={{ margin: 0 }}>Vulnerable Groups Identification</h2>
              <button
                className="consultant-vulnerabilities__print-btn"
                onClick={() => window.print()}
                aria-label="Print vulnerable groups"
              >
                <Printer className="consultant-vulnerabilities__print-icon" />
                Print
              </button>
            </div>
            <div className="consultant-vulnerabilities__table-container">
              <table className="consultant-vulnerabilities__table">
                <thead>
                  <tr className="consultant-vulnerabilities__table-header">
                    <th className="consultant-vulnerabilities__table-header-cell">Group Name</th>
                    <th className="consultant-vulnerabilities__table-header-cell">Risk Score</th>
                    <th className="consultant-vulnerabilities__table-header-cell">SOWB RISK</th>
                    <th className="consultant-vulnerabilities__table-header-cell">Primary Stressor</th>
                    <th className="consultant-vulnerabilities__table-header-cell">Headcount</th>
                  </tr>
                </thead>
                <tbody className="consultant-vulnerabilities__table-body">
                  {vulnerabilitiesData.length === 0 ? (
                    <tr className="consultant-vulnerabilities__table-row">
                      <td colSpan={5} className="consultant-vulnerabilities__table-cell consultant-vulnerabilities__table-cell--empty">
                        <div className="consultant-vulnerabilities__empty-state">
                          <GraduationCap className="consultant-vulnerabilities__empty-icon" />
                          <p>No groups found or no assessment data available</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    vulnerabilitiesData.map((group) => {
                      const riskScore = getRiskScore(group.overallPercent0to100);
                      const riskBarWidth = `${riskScore}%`;
                      const primaryVulnerability = group.lowestAttributes[0]?.attribute || 'None';
                      const formattedVulnerability = formatAttributeName(primaryVulnerability);

                      let riskBarClass = 'consultant-vulnerabilities__risk-bar-fill';
                      let riskScoreClass = 'consultant-vulnerabilities__risk-score';
                      let sowbBadgeClass = 'consultant-vulnerabilities__sowb-badge';

                      if (riskScore >= 80) {
                        riskBarClass += ' consultant-vulnerabilities__risk-bar-fill--high';
                        riskScoreClass += ' consultant-vulnerabilities__risk-score--high';
                        sowbBadgeClass += ' consultant-vulnerabilities__sowb-badge--high-risk';
                      } else if (riskScore >= 60) {
                        riskBarClass += ' consultant-vulnerabilities__risk-bar-fill--moderate';
                        riskScoreClass += ' consultant-vulnerabilities__risk-score--moderate';
                        sowbBadgeClass += ' consultant-vulnerabilities__sowb-badge--moderate-risk';
                      } else {
                        riskBarClass += ' consultant-vulnerabilities__risk-bar-fill--low';
                        riskScoreClass += ' consultant-vulnerabilities__risk-score--low';
                        sowbBadgeClass += ' consultant-vulnerabilities__sowb-badge--low-risk';
                      }

                      // Map attribute to badge type
                      const attributeBadgeMap: Record<string, string> = {
                        spiritual: 'spiritual',
                        nutritional: 'nutritional',
                        physical: 'physical',
                        emotioPsychoRational: 'emotional',
                        legendarial: 'legendarial',
                        religioTraditioCultural: 'cultural',
                        financial: 'financial',
                        environmental: 'environmental',
                        socioRelational: 'relational',
                        occupational: 'occupational'
                      };

                      const badgeType = attributeBadgeMap[primaryVulnerability] || 'general';

                      return (
                        <tr key={group.groupId} className="consultant-vulnerabilities__table-row">
                          <td className="consultant-vulnerabilities__table-cell consultant-vulnerabilities__table-cell--name">
                            {group.groupName}
                          </td>
                          <td className="consultant-vulnerabilities__table-cell">
                            <div className="consultant-vulnerabilities__risk-bar">
                              <progress
                                className={riskBarClass}
                                value={riskScore}
                                max={100}
                              />
                            </div>
                            <span className={riskScoreClass}>{riskScore}</span>
                          </td>
                          <td className="consultant-vulnerabilities__table-cell">
                            <span className={`${sowbBadgeClass} consultant-vulnerabilities__sowb-badge--${badgeType}`}>
                              {formattedVulnerability}
                            </span>
                          </td>
                          <td className="consultant-vulnerabilities__table-cell">
                            {primaryVulnerability !== 'None'
                              ? `${formattedVulnerability} Focus Needed`
                              : 'Well Balanced'
                            }
                          </td>
                          <td className="consultant-vulnerabilities__table-cell">
                            {group.memberCount}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="consultant-vulnerabilities__footer-actions">
          <div className="consultant-vulnerabilities__ownership-toggle" aria-label="Ownership filter">
            {user?.role === 'Admin' ? (
              <>
                <button
                  type="button"
                  className={`consultant-vulnerabilities__ownership-btn ${ownershipFilter === 'locally_owned' ? 'consultant-vulnerabilities__ownership-btn--active' : ''}`}
                  onClick={() => setOwnershipFilter('locally_owned')}
                >
                  Locally Owned
                </button>
                <button
                  type="button"
                  className={`consultant-vulnerabilities__ownership-btn ${ownershipFilter === 'enterprise_owned' ? 'consultant-vulnerabilities__ownership-btn--active' : ''}`}
                  onClick={() => setOwnershipFilter('enterprise_owned')}
                >
                  Enterprise Owned
                </button>
              </>
            ) : (
              <div className={`consultant-vulnerabilities__ownership-badge consultant-vulnerabilities__ownership-badge--${(user?.role === 'Enterprise' || user?.createdBy) ? 'enterprise' : 'personal'}`}>
                {(user?.role === 'Enterprise' || user?.createdBy) ? 'Enterprise' : 'Personal'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultantVulnerabilitiesPage;
